/**
 * WorldMap — dependency-light interactive world map (d3-geo + topojson).
 * Countries with published stories glow gold; clicking one fires
 * onSelect(dbCountryName). Used full-size on /map and as a teaser on home.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { geoNaturalEarth1, geoPath } from "d3-geo";
import { feature } from "topojson-client";

// Map free-text DB country names → topojson `properties.name`.
// A value may be an ARRAY — region names (e.g. "Balkans") highlight
// every country in the group.
const ALIASES = {
  "usa": "united states of america",
  "us": "united states of america",
  "united states": "united states of america",
  "america": "united states of america",
  "uk": "united kingdom",
  "britain": "united kingdom",
  "great britain": "united kingdom",
  "england": "united kingdom",
  "scotland": "united kingdom",
  "wales": "united kingdom",
  "northern ireland": "united kingdom",
  "türkiye": "turkey",
  "turkiye": "turkey",
  "uae": "united arab emirates",
  "czech republic": "czechia",
  "holland": "netherlands",
  "the netherlands": "netherlands",
  "burma": "myanmar",
  "ivory coast": "côte d'ivoire",
  "drc": "dem. rep. congo",
  "democratic republic of congo": "dem. rep. congo",
  "democratic republic of the congo": "dem. rep. congo",
  "republic of the congo": "congo",
  "bosnia": "bosnia and herz.",
  "bosnia and herzegovina": "bosnia and herz.",
  "north macedonia": "macedonia",
  "south korea": "south korea",
  "north korea": "north korea",
  "russian federation": "russia",
  "dominican republic": "dominican rep.",
  "central african republic": "central african rep.",
  "south sudan": "s. sudan",
  "swaziland": "eswatini",
  "gaza": "palestine",
  "gaza strip": "palestine",
  "west bank": "palestine",
  "palestinian territories": "palestine",
  "northern cyprus": "n. cyprus",
  "balkans": [
    "serbia", "bosnia and herz.", "albania", "macedonia",
    "montenegro", "kosovo", "croatia", "slovenia", "bulgaria",
  ],
  "the balkans": [
    "serbia", "bosnia and herz.", "albania", "macedonia",
    "montenegro", "kosovo", "croatia", "slovenia", "bulgaria",
  ],
  "yugoslavia": [
    "serbia", "bosnia and herz.", "macedonia",
    "montenegro", "kosovo", "croatia", "slovenia",
  ],
  "former yugoslavia": [
    "serbia", "bosnia and herz.", "macedonia",
    "montenegro", "kosovo", "croatia", "slovenia",
  ],
  "scandinavia": ["norway", "sweden", "denmark"],
  "middle east": [
    "israel", "palestine", "jordan", "lebanon", "syria", "iraq",
    "saudi arabia", "yemen", "oman", "united arab emirates",
    "qatar", "kuwait", "iran",
  ],
};

const W = 980;
const H = 500;

export default function WorldMap({ countries = [], onSelect, interactive = true }) {
  const [geos, setGeos] = useState(null);
  const [hover, setHover] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => {
    fetch("/world-110m.json")
      .then((r) => r.json())
      .then((topo) => setGeos(feature(topo, topo.objects.countries).features))
      .catch(() => setGeos([]));
  }, []);

  // normalized topojson name -> {name: db name, count}
  const countMap = useMemo(() => {
    const m = {};
    for (const c of countries) {
      const norm = (c.name || "").trim().toLowerCase();
      if (!norm) continue;
      const alias = ALIASES[norm] || norm;
      const targets = Array.isArray(alias) ? alias : [alias];
      for (const target of targets) {
        m[target] = { name: c.name, count: (m[target]?.count || 0) + c.count };
      }
    }
    return m;
  }, [countries]);

  const maxCount = useMemo(
    () => Math.max(1, ...Object.values(countMap).map((v) => v.count)),
    [countMap]
  );

  const path = useMemo(() => {
    const projection = geoNaturalEarth1().fitExtent(
      [[6, 6], [W - 6, H - 6]], { type: "Sphere" });
    return geoPath(projection);
  }, []);

  const move = (e, info, geoName) => {
    if (!boxRef.current) return;
    const rect = boxRef.current.getBoundingClientRect();
    setHover({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      label: info ? `${info.name} · ${info.count} ${info.count === 1 ? "story" : "stories"}` : geoName,
      has: !!info,
    });
  };

  if (geos === null) {
    return <div className="w-full aspect-[980/500] animate-pulse rounded-xl bg-white/[0.04]" />;
  }

  return (
    <div ref={boxRef} className="relative w-full" data-testid="world-map">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block">
        {geos.map((g, i) => {
          const info = countMap[(g.properties?.name || "").toLowerCase()];
          const opacity = info ? 0.55 + 0.45 * (info.count / maxCount) : 1;
          return (
            <path
              key={g.id || i}
              d={path(g)}
              fill={info ? "#EAB308" : "rgba(255,255,255,0.07)"}
              fillOpacity={opacity}
              stroke="rgba(0,0,0,0.5)"
              strokeWidth={0.5}
              style={{
                cursor: info && interactive ? "pointer" : "default",
                transition: "fill-opacity 150ms",
              }}
              onClick={() => info && interactive && onSelect && onSelect(info.name)}
              onMouseMove={interactive ? (e) => move(e, info, g.properties?.name) : undefined}
              onMouseLeave={interactive ? () => setHover(null) : undefined}
              data-testid={info ? `map-country-${info.name}` : undefined}
            />
          );
        })}
        {/* Pin dots — small matched countries (Cyprus, Gaza, Malta…)
            are near-invisible at world scale; a glowing marker keeps
            them clickable and discoverable. */}
        {geos.map((g, i) => {
          const info = countMap[(g.properties?.name || "").toLowerCase()];
          if (!info) return null;
          if (path.area(g) > 90) return null; // big enough to see already
          const [cx, cy] = path.centroid(g);
          if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
          return (
            <g
              key={`pin-${g.id || i}`}
              style={{ cursor: interactive ? "pointer" : "default" }}
              onClick={() => interactive && onSelect && onSelect(info.name)}
              onMouseMove={interactive ? (e) => move(e, info, g.properties?.name) : undefined}
              onMouseLeave={interactive ? () => setHover(null) : undefined}
              data-testid={`map-pin-${info.name}`}
            >
              <circle cx={cx} cy={cy} r={9} fill="#EAB308" opacity={0.25}>
                <animate attributeName="r" values="7;11;7" dur="2.4s" repeatCount="indefinite" />
              </circle>
              <circle cx={cx} cy={cy} r={4} fill="#EAB308" stroke="rgba(0,0,0,0.6)" strokeWidth={1} />
            </g>
          );
        })}
      </svg>
      {hover && interactive && (
        <div
          className={`absolute pointer-events-none px-2.5 py-1.5 rounded-md text-xs shadow-xl border ${
            hover.has
              ? "bg-[#1a170f] border-[#EAB308]/50 text-[#EAB308]"
              : "bg-black/80 border-white/10 text-white/70"
          }`}
          style={{ left: hover.x + 14, top: hover.y - 10, zIndex: 20 }}
        >
          {hover.label}
        </div>
      )}
    </div>
  );
}
